import TableCell from '@mui/material/TableCell';
import React from 'react';
import { Typography, TextField, InputAdornment, Tooltip } from '@mui/material';
import { CloseRounded, DoneRounded } from '@mui/icons-material';
import { RowSsrObjectType } from '../../../types/ancillaryCostTableRowTypes';
import { useBookingContext } from '../../../hooks/use-booking-context';

interface props {
  row: RowSsrObjectType;
  surchargeValues: { [key: string]: string };
  handleInputChange: (id: any, value: any) => void;
}

const AdjustedSurchargeCell: React.FC<props> = ({ row, surchargeValues, handleInputChange }) => {
  const { costFormatter, numberFormatter, decimalSeparator } = useBookingContext();
  const zeroValue = numberFormatter.format(0);
  const adjustedAllowedCharsRegex = new RegExp(`(?!^-)[^0-9${decimalSeparator.replace('.', '\\.')}]`, 'g');

  const isAllowed = (surcharge: number, amount: number) =>
    Number.isInteger(surcharge / amount) || Number((surcharge / amount).toFixed(2)) === surcharge / amount;

  const extractAndCheck = (newAmount: number, originalAmount: number): number => {
    if (originalAmount === newAmount) {
      return newAmount || originalAmount;
    } else if (originalAmount < newAmount) {
      return newAmount - originalAmount;
    } else {
      return newAmount;
    }
  };

  const boughtAmount = extractAndCheck(parseInt(row.new.split(' x ')[0]), parseInt(row.original.split(' x ')[0])); // not sure if this string is consistent across all SSRs, if not, an alternative approach might be needed
  const isPriceDifferent =
    parseInt(row.original.split(' x ')[1]) !== 0 && row.new.split(' x ')[1] > row.original.split(' x ')[1];

  return (
    <TableCell align="right" sx={{ borderTop: '1px solid #737373' }}>
      {row.label !== 'FLIGHT' && row.code ? (
        <>
          <TextField
            inputProps={{
              style: {
                width: '100px',
                height: '31px',
                padding: '4px',
                lineHeight: '23.4px',
                fontSize: '18px',
                textAlign: 'right',
                borderRadius: '4px',
                border: surchargeValues[`${row.code}-${row.new[0]}-${row.original[0]}`]
                  ? parseInt(surchargeValues[`${row.code}-${row.new[0]}-${row.original[0]}`]) !== row.surcharge
                    ? '1px solid #30B575'
                    : '1px solid transparent'
                  : 'none',
              },
            }}
            value={surchargeValues[`${row.code}-${row.new[0]}-${row.original[0]}`] ?? surchargeValues[row.code] ?? 0}
            onChange={e => {
              const numericValue =
                e.target.value !== '' && e.target.value !== zeroValue
                  ? e.target.value.replace(adjustedAllowedCharsRegex, '')
                  : zeroValue;
              handleInputChange(`${row.code}-${row.new[0]}-${row.original[0]}`, numericValue);
            }}
            onBlur={e => {
              const numericValue = e.target.value.replace(adjustedAllowedCharsRegex, '');
              const parsedValue = parseFloat(numericValue.replace(decimalSeparator, '.'));
              const formattedValue = numberFormatter.format(parsedValue);
              handleInputChange(`${row.code}-${row.new[0]}-${row.original[0]}`, formattedValue);
            }}
          />
          {(surchargeValues[`${row.code}-${row.new[0]}-${row.original[0]}`]
            ? parseInt(surchargeValues[`${row.code}-${row.new[0]}-${row.original[0]}`]) !== row.surcharge
            : false) && (
            <InputAdornment position="end" sx={{ marginTop: '-20px', position: 'absolute', width: '0', height: '0' }}>
              {isAllowed(parseFloat(surchargeValues[`${row.code}-${row.new[0]}-${row.original[0]}`]), boughtAmount) ? (
                <Tooltip
                  title={
                    isPriceDifferent
                      ? `Midoco prices may need manual adjustments. Please check Midoco after finalizing changes.`
                      : ''
                  }
                  placement="top"
                >
                  <DoneRounded style={{ color: 'green' }} />
                </Tooltip>
              ) : (
                <Tooltip title={<>Indivisable by bought SSR amount {boughtAmount}</>} placement="top">
                  <CloseRounded style={{ color: 'red' }} />
                </Tooltip>
              )}
            </InputAdornment>
          )}
        </>
      ) : (
        <Typography sx={{ textAlign: 'right' }}>{row.label && costFormatter.format(row.surcharge)}</Typography>
      )}
    </TableCell>
  );
};
export default AdjustedSurchargeCell;
